import React, { memo, useCallback, useState } from 'react';
import { MixedGroupedTournamentModel } from '../../mixed-grouped.types';
import { BemMethod, createBem } from '@om-fe/utils';
import MarketGroupsTournamentOutrights from '@om-fe/core/components/market-groups/market-groups-tournament-outrights.tsx'
import { GroupKeys } from '@om-fe/core/components/market-groups/market-groups.constants.ts'
import TournamentOutrights from '@om-fe/core/components/tournament-outrights/tournament-outrights';
import { DisciplineRouteType } from '@om-fe/core/components/discipline-anchor/discipline-route.utils';
import { MixedGroupTournamentHeader } from '../tournament-header/mixed-group-tournament-header';
import { UseSimpleStore, useSimpleStoreItem } from '@om-fe/store';
import getConfig, { OMFE2ConfigModel } from '@om-fe/core/utils/config';
import { DisciplinesType } from '@om-fe/models/dist/models/types/disciplines';

const bem: BemMethod = createBem('MixedGroupedOutrightsItem');

export interface MixedGroupedOutrightsItemProps {
    readonly useStore: UseSimpleStore<MixedGroupedTournamentModel>;
    readonly id: string;
    readonly sportPageType: DisciplineRouteType;
    readonly count: number;
}

export const MixedGroupedOutrightsItem = memo(function MixedGroupedOutrightsItemComponent({
    useStore,
    id,
    sportPageType,
    count,
}: MixedGroupedOutrightsItemProps,
) {
    

    const tournament: MixedGroupedTournamentModel = useSimpleStoreItem<MixedGroupedTournamentModel>(useStore, id);
    const config: OMFE2ConfigModel = getConfig();
    const enableExpandAllEventsOnPriceBoost: boolean = config.matchListConfig?.enableExpandAllEventsOnPriceBoost ?? false;
    const isPriceBoost: boolean = tournament.sportId === DisciplinesType.PriceBoost;

    const initiallyOpen: boolean = count === 0 || (isPriceBoost && enableExpandAllEventsOnPriceBoost);
    const [show, setShow] = useState<boolean>(initiallyOpen);
    const [hasOpened, setHasOpened] = useState<boolean>(initiallyOpen);

    const showEndTime: boolean | undefined = config.matchListConfig?.outrights?.showEndTime;
    const dateFormat: string | undefined = config.dateFormat?.FormatFullDateTime;
    const outrightMarketGroup: boolean = getConfig().outrightMarketGroup ?? false;
    const [activeMarketGroup, setActiveMarketGroup] = useState<string | undefined>(
        outrightMarketGroup ? undefined : GroupKeys.All,
    );
    const [loadedMarketGroups, setLoadedMarketGroups] = useState<string[]>(
        outrightMarketGroup ? [] : [GroupKeys.All],
    );
    const enabledDash: boolean = config.bannerMatrix?.enabledDashOnPriceboost || false;

    const toggle = useCallback(() => {
        setHasOpened(true);
        setShow((value: boolean): boolean => !value);
    }, []);

    const handleActiveMarketGroup = useCallback((marketGroup: string): void => {
        setActiveMarketGroup(marketGroup);
        setLoadedMarketGroups((currentMarketGroups: string[]): string[] =>
            currentMarketGroups.includes(marketGroup)
                ? currentMarketGroups
                : [...currentMarketGroups, marketGroup]
        );
    }, []);

    if ((!tournament.isTopLevelTournament && !tournament.numberOfOutrightMarkets) || (tournament.isTopLevelTournament && !tournament.numberOfOutrightMarkets && !tournament.numberOfOutrightMarketsForSubtournaments)) {
        return null;
    }

    return (
        <div className={bem()}>
            <MixedGroupTournamentHeader
                useStore={useStore}
                sportPageType={sportPageType}
                id={id}
                open={show}
                onToggle={toggle}
            />
            {(show || hasOpened) && (
                <div
                    className={bem('Content')}
                    hidden={!show}
                >
                    {outrightMarketGroup &&
                        <MarketGroupsTournamentOutrights 
                            tournamentId={tournament.id} 
                            instanceId={`MixedMarketGroupOutrightsItem-${tournament.id}`}
                            activeMarketGroupId={activeMarketGroup}
                            setActiveMarketGroup={handleActiveMarketGroup}
                        />
                    }
                    {!outrightMarketGroup && activeMarketGroup &&
                        <TournamentOutrights
                            instanceId={`MixedGroupedOutrightsItem-${id}`}
                            tournamentId={tournament.id}
                            disciplineId={tournament.sportId}
                            showEndTime={showEndTime}
                            endTime={tournament.endTime}
                            dateFormat={dateFormat}
                            activeMarketGroup={activeMarketGroup}
                            setActiveMarketGroup={setActiveMarketGroup}
                            columnByBreakpoint={true}
                            showDash={enabledDash && tournament.sportId === DisciplinesType.PriceBoost}
                        />
                    }
                    {outrightMarketGroup
                        && loadedMarketGroups.map((marketGroup: string) => (
                            <div
                                key={marketGroup}
                                hidden={marketGroup !== activeMarketGroup}
                            >
                                <TournamentOutrights
                                    instanceId={`MixedGroupedOutrightsItem-${id}-${marketGroup}`}
                                    tournamentId={tournament.id}
                                    disciplineId={tournament.sportId}
                                    showEndTime={showEndTime}
                                    endTime={tournament.endTime}
                                    dateFormat={dateFormat}
                                    activeMarketGroup={marketGroup}
                                    columnByBreakpoint={true}
                                    showDash={enabledDash && tournament.sportId === DisciplinesType.PriceBoost}
                                />
                            </div>
                        ))
                    }
                </div>
            )}
        </div>
    );
});
